import type { TicketSubStage } from '@kilat/shared';

/**
 * Pure wash sub-stage progression shared by the queue board's stepper.
 *
 * A ticket in `inWashingProcess` moves through three internal sub-stages:
 *
 *   (none) → inBay → drying → qualityCheck → (end)
 *
 * `SUB_STAGE_ORDER` is the canonical forward order. `nextSubStage` maps the
 * current sub-stage to the one after it, returning `null` when there is no
 * next stage (already at `qualityCheck`, or an unrecognised value) so the
 * caller can disable the "advance" control instead of looping back. A ticket
 * that has not entered any sub-stage yet (`undefined`/`null`) advances to the
 * first stage, `inBay`.
 */
export const SUB_STAGE_ORDER = ['inBay', 'drying', 'qualityCheck'] as const;

export type WashSubStage = (typeof SUB_STAGE_ORDER)[number];

/** Narrowing guard — is `value` one of the known wash sub-stages? */
export function isWashSubStage(value: unknown): value is WashSubStage {
  return typeof value === 'string' && (SUB_STAGE_ORDER as readonly string[]).includes(value);
}

/**
 * The next sub-stage after `current`, or `null` when there is none.
 *   - `undefined`/`null`  → `inBay` (start the progression)
 *   - `inBay`             → `drying`
 *   - `drying`            → `qualityCheck`
 *   - `qualityCheck`      → `null` (end — nothing further)
 *   - anything unknown    → `null`
 */
export function nextSubStage(current: TicketSubStage | null | undefined): WashSubStage | null {
  if (current == null) return SUB_STAGE_ORDER[0];
  const idx = (SUB_STAGE_ORDER as readonly string[]).indexOf(current);
  if (idx < 0) return null;
  return SUB_STAGE_ORDER[idx + 1] ?? null;
}
